import { Block } from "baseui/block";
import DataTable, { FilterTypes } from "../../components/data-table/data-table";
import { useGet } from "@/hooks/apiHooks";
import { Tag, TagKind } from "baseui/tag";
import { PLACEMENT, StatefulTooltip } from "baseui/tooltip";
import { CandidateData, Job } from "@/network/definitions/job";
import { VetsmithText } from "@/components/VetsmithText";

export const getApplicationReviewTag = (status: string, isFailed = false) => {
    let tagKind: TagKind;
    let statusText: string;
    // A terminal review status always wins. Only a still-pending row surfaces as "Failed"
    // (a processing/resume-review crash that left review_status at 'pending') instead of
    // "Pending", so partners know the upload didn't go through.
    if (status?.toLowerCase() === 'approved') {
        tagKind = TagKind.positive;
        statusText = 'Approved';
    } else if (status?.toLowerCase() === 'rejected') {
        tagKind = TagKind.negative;
        statusText = 'Rejected';
    } else if (isFailed) {
        tagKind = TagKind.negative;
        statusText = 'Failed';
    } else {
        tagKind = TagKind.warning
        statusText = 'Pending';
    }
    return <Tag
        closeable={false}
        kind={tagKind}
        overrides={{
            Root: {
                style: {
                    height: '24px',
                    lineHeight: '20px',
                    paddingTop: '0px',
                    paddingBottom: '0px',
                    marginTop: '0px',
                    marginBottom: '0px',
                },
            },
        }}
    >{statusText}</Tag>
}

export const getReviewerComments = (reviewerComments: string | null) => {
    const comments = reviewerComments?.trim()
    if (comments) {
        return (
            <StatefulTooltip
                content={() => <Block style={{ maxWidth: 300 }}>{comments}</Block>}
                placement={PLACEMENT.top}
                showArrow
            >
                <Block style={{ maxWidth: 400, textOverflow: 'ellipsis', overflow: 'hidden' }}>{comments}</Block>
            </StatefulTooltip>
        );
    }
    return ''
}

const CandidateList = () => {
    const {
        isLoading,
        data: {
            data: jobs = [],
        } = {},
    } = useGet<Job>('agency-api/jobs');

    const getJobRequestStatus = (job_request_status: string | null) => {
        return job_request_status || '-'
    }

    const columnsConfig = [
        { field: 'developer_id', headerName: 'Candidate id', sortable: false, },
        { field: 'email', headerName: 'Email', sortable: true, filterType: FilterTypes.Text, pinned: true },
        { field: 'name', headerName: 'Name', filterType: FilterTypes.Text },
        { field: 'resume', headerName: 'Resume link', render: (row) => <a href={row.resume} target="_blank" rel="noopener noreferrer">Link</a> },
        {
            field: 'job_title',
            headerName: 'Job role',
            filterCustomName: 'job_id',
            filterType: FilterTypes.Select,
            filterValues: (jobs as Job[]).map((job: Job) => ({
                label: `${job.id}-${job.role}`,
                value: job.id
            })),
            render: (row: CandidateData) => `${row.job_id} - ${row.job_title}`
        },
        {
            field: 'expected_rate',
            headerName: 'Rate',
        },
        {
            field: 'location',
            headerName: 'Location',
        },
        {
            field: 'notice_period',
            headerName: 'Availability',
            render: (row: CandidateData) => `${row.notice_period} weeks`
        },
        {
            field: 'note',
            headerName: 'Agency notes',
        },
        {
            field: 'application_review_status',
            headerName: 'Review status',
            render: (row: CandidateData) =>
                getApplicationReviewTag(
                    row.application_review_status,
                    row.application_processing_status === 'failed',
                )
        },
        {
            field: 'rejection_reason',
            headerName: 'Rejection reason',
            render: (row: CandidateData) =>
                getReviewerComments(row.reviewer_comments || row.processing_failed_reason),
        },
        {
            field: 'job_request_link',
            headerName: 'Interest Check Form',
            render: (row: CandidateData) => getJobRequestStatus(row.job_request_status),
        },
        {
            field: 'vetsmith',
            headerName: 'Automated or pre-screening Test',
            render: (row: CandidateData) => <VetsmithText item={row} />
        },
        {
            field: 'mti_status',
            headerName: 'First interview round status',
        },
        {
            field: 'onboarding_status',
            headerName: 'Onboarding status',
        },
        {
            field: 'trial_start_date',
            headerName: 'Trial start date',
        },
        {
            field: 'final_developer_rate',
            headerName: 'Final developer rate',
        },
        { field: 'uploaded_date', headerName: 'Uploaded On' },
    ];

    return (
        <Block>
            <DataTable<CandidateData>
                apiEndpoint="agency-api/candidates"
                initialColumnsConfig={columnsConfig as any}
                loading={isLoading}
            />
        </Block>
    )
}

export default CandidateList;
